import {
  OnboardingTarget,
  onboardingTarget,
} from "../onboarding/onboardingTargets";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AnimatePresence, motion } from "framer-motion";
import {
  selectCurrentEnvironmentId,
  selectEnvironmentInteractionPoints,
  selectEnvironmentLayers,
  selectCurrentEnvironmentThumbnail,
} from "../../app/slices/EnvironmentSlice";
import {
  selectIsPlaying,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedEnvironmentLayerIds,
  selectSelectedTake3D,
  loadSelectedEnvironmentLayerIds,
  setSelectedEnvironmentLayerIds,
  setImageBounds,
  setViewportBounds,
} from "../../app/slices/TrainingSlice";
import Interaction, { InteractionType } from "../../models/interactions/Interaction";
import ViewportMap3D from "../leaflet/ViewportMap3D";
import { calculateContainedImageBounds } from "../training_preview/ImageBounds";
import TrainingPreviewOverlay from "../training_preview/TrainingPreviewOverlay";
import CameraWidget from "./CameraWidget";
import "./Viewport3D.css";
import ViewportTools from "../viewport_tools/ViewportTools";
import PropLibrary from "../prop_library/PropLibrary";
import useViewport3D from "./Viewport3D.hook";
import {
  closeSequencePositionPicker,
  SelectedTab,
  selectSelectedTab,
  selectSequencePositionPicker,
  selectShowEnvironmentProps,
  setShowEnvironmentProps,
} from "../../app/slices/GlobalSlice";
import QuickActorModal from "./QuickActorModal";
import ViewportLayerFilterControl from "./ViewportLayerFilterControl";
import ViewportActorStateWindow from "./ViewportActorStateWindow";
import ViewportTransitionWindow from "./ViewportTransitionWindow";
import ActorPresetEditor from "../actor_presets/ActorPresetEditor";
import PropPresetEditor from "../prop_presets/PropPresetEditor";
import ViewportEnvironmentPropsControl from "./ViewportEnvironmentPropsControl";

export default function Viewport3D() {
  const url = useSelector(selectCurrentEnvironmentThumbnail);

  const dispatch = useDispatch();
  const imgRef = useRef<HTMLImageElement>(null);
  const isPlaying = useSelector(selectIsPlaying);
  const selectedElement = useSelector(selectSelectedElement);
  const selectedElementType = useSelector(selectSelectedElementType);
  const currentEnvironmentId = useSelector(selectCurrentEnvironmentId);
  const currentTab = useSelector(selectSelectedTab);
  const layers = useSelector(selectEnvironmentLayers);
  const interactionPoints = useSelector(selectEnvironmentInteractionPoints);
  const selectedLayerIds = useSelector(selectSelectedEnvironmentLayerIds);
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const showEnvironmentProps = useSelector(selectShowEnvironmentProps);
  const [openViewportDropdown, setOpenViewportDropdown] = useState<
    "layers" | "environmentProps" | null
  >(null);

  const {
    closePropLibrary,
    isPropLibraryOpen,
    isQuickActorModalOpen,
    closeQuickActorModal,
  } = useViewport3D();

  const selectedTake = useSelector(selectSelectedTake3D);
  const isCameraSelected = useMemo(() => {
    return selectedElement?.id === selectedTake?.mainCamera.id;
  }, [selectedElement, selectedTake]);
  const isSequenceContext = useMemo(() => {
    const selectedInteraction =
      selectedElementType === "interaction"
        ? (selectedElement as Interaction | undefined)
        : undefined;

    return (
      selectedElementType === "takeSequence" ||
      selectedInteraction?.type === InteractionType.Sequence
    );
  }, [selectedElement, selectedElementType]);
  const isTransitionSelected = selectedElementType === "transition";
  const presetEditor =
    currentTab === SelectedTab.ActorPresets ? (
      <ActorPresetEditor />
    ) : currentTab === SelectedTab.PropPresets ? (
      <PropPresetEditor />
    ) : null;

  const availableLayers = useMemo(() => {
    if (layers.length > 0) return layers;

    const uniqueLayers = Array.from(
      new Set(
        interactionPoints
          .map((position) => position.layer)
          .filter((layer): layer is string => !!layer)
      )
    );

    return uniqueLayers.map((id) => ({ id, name: id }));
  }, [layers, interactionPoints]);

  const availableLayerIdsKey = useMemo(() => {
    return availableLayers.map((layer) => layer.id).join("|");
  }, [availableLayers]);

  useEffect(() => {
    if (availableLayers.length === 0) {
      if (selectedLayerIds.length > 0) {
        dispatch(loadSelectedEnvironmentLayerIds([]));
      }
      return;
    }

    const defaultLayer = availableLayers.find((layer) => layer.id === "default")
      ?.id;
    const firstLayerId = availableLayers[0].id;
    const validSelection = selectedLayerIds.filter((id) =>
      availableLayers.some((layer) => layer.id === id)
    );
    if (validSelection.length > 0) {
      const unchanged =
        validSelection.length === selectedLayerIds.length &&
        validSelection.every((id, index) => id === selectedLayerIds[index]);
      if (!unchanged) {
        dispatch(loadSelectedEnvironmentLayerIds(validSelection));
      }
      return;
    }

    const fallback = [defaultLayer ?? firstLayerId];
    const fallbackUnchanged =
      fallback.length === selectedLayerIds.length &&
      fallback.every((id, index) => id === selectedLayerIds[index]);
    if (!fallbackUnchanged) {
      dispatch(loadSelectedEnvironmentLayerIds(fallback));
    }
  }, [
    currentEnvironmentId,
    availableLayerIdsKey,
    availableLayers,
    dispatch,
    selectedLayerIds,
  ]);

  const onLoad = useCallback(async () => {
    // get actual image size
    if (imgRef.current) {
      let img = new Image();
      img.src = url === undefined ? "" : url!;

      img.onload = async () => {
        var b = imgRef.current?.getBoundingClientRect();
        if (b) {
          const imgBounds = calculateContainedImageBounds(
            img.width,
            img.height,
            b.width,
            b.height
          );

          dispatch(
            setViewportBounds({
              x: b.x,
              y: b.y,
              width: b.width,
              height: b.height,
            })
          );
          dispatch(setImageBounds(imgBounds));
        }
      };
    }
  }, [dispatch, setImageBounds, setViewportBounds, imgRef, url]);

  const onWindowResize = useCallback(() => {
    onLoad();
  }, [onLoad]);

  // resize observer
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === imgRef.current) {
          onLoad();
        }
      }
    });

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [onLoad]);

  useEffect(() => {
    // window resize
    window.addEventListener("resize", onWindowResize);

    return () => window.removeEventListener("resize", onWindowResize);
  });

  // resize after stop playing -> the transition messes up the calculations
  useEffect(() => {
    if (!isPlaying) {
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
      const waitResize = async () => {
        await delay(150);
        onWindowResize();
      };

      waitResize();
    }
  }, [isPlaying, onWindowResize]);

  useEffect(() => {
    if (!sequencePositionPicker.isOpen) return;
    const isValidContext =
      sequencePositionPicker.positionType === "takeLocation"
        ? selectedElementType === "take"
        : isSequenceContext;
    if (currentTab !== SelectedTab.Timeline || !isValidContext) {
      dispatch(closeSequencePositionPicker());
    }
  }, [
    currentTab,
    dispatch,
    isSequenceContext,
    selectedElementType,
    sequencePositionPicker.isOpen,
    sequencePositionPicker.positionType,
  ]);

  return (
    <div
      className={"viewport3d-root relative"}
      {...onboardingTarget(OnboardingTarget.Editor3DViewport)}
    >
      {selectedElement &&
        selectedElementType !== "actor" &&
        !isCameraSelected &&
        !isTransitionSelected &&
        !sequencePositionPicker.isOpen && <TrainingPreviewOverlay />}
      <ViewportMap3D
        visibleLayerIds={selectedLayerIds}
        onBackgroundClick={
          sequencePositionPicker.isOpen
            ? () => dispatch(closeSequencePositionPicker())
            : undefined
        }
      />
      <CameraWidget />
      {presetEditor && (
        <aside
          aria-label="viewport-preset-editor"
          className="viewport3d-preset-editor"
        >
          <div className="viewport3d-preset-editor__panel">
            {presetEditor}
          </div>
        </aside>
      )}
      <ViewportActorStateWindow />
      <ViewportTransitionWindow />
      <ViewportTools>
        {currentTab === SelectedTab.Timeline && (
          <>
            {availableLayers.length > 0 && (
              <ViewportLayerFilterControl
                layers={availableLayers}
                selectedLayerIds={selectedLayerIds}
                onChange={(ids) => dispatch(setSelectedEnvironmentLayerIds(ids))}
                isOpen={openViewportDropdown === "layers"}
                onOpenChange={(isOpen) =>
                  setOpenViewportDropdown(isOpen ? "layers" : null)
                }
              />
            )}
            <ViewportEnvironmentPropsControl
              checked={showEnvironmentProps}
              onChange={(checked) => dispatch(setShowEnvironmentProps(checked))}
              isOpen={openViewportDropdown === "environmentProps"}
              onOpenChange={(isOpen) =>
                setOpenViewportDropdown(isOpen ? "environmentProps" : null)
              }
            />
          </>
        )}
      </ViewportTools>
      <AnimatePresence initial={false}>
        {sequencePositionPicker.isOpen &&
          currentTab === SelectedTab.Timeline && (
            <>
              <motion.div
                key="sequence-picker-toolbar"
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="absolute left-[80px] top-[16px] z-[1000]"
              >
                <button
                  type="button"
                  className="flex items-center gap-[6px] rounded-full border border-creator-border bg-creator-surface px-[12px] py-[8px] text-[12px] font-medium text-creator-text shadow-md transition-colors hover:bg-creator-primary-background"
                  onClick={() => dispatch(closeSequencePositionPicker())}
                >
                  <ArrowBackIcon className="text-[16px]" fontSize="inherit" />
                  {sequencePositionPicker.positionType === "takeLocation"
                    ? "Back to take"
                    : "Back to sequence"}
                </button>
              </motion.div>
            </>
          )}
      </AnimatePresence>
      {currentTab === SelectedTab.Timeline && (
        <>
          <PropLibrary
            isOpen={isPropLibraryOpen}
            onClose={closePropLibrary}
            title={"Add New Prop"}
          />
          <QuickActorModal
            isOpen={isQuickActorModalOpen}
            onClose={closeQuickActorModal}
            title={"Add New Actor"}
            width={"90vw"}
            height={"90vh"}
          />
        </>
      )}
    </div>
  );
}
